import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { validatePermissionKey } from '../config/permissionRegistry';
import { resolveEventColor } from '../config/eventColors';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

// hex (#rgb or #rrggbb) -> "H S% L%" string for CSS custom properties (shadcn format)
const hexToHsl = (hex) => {
    if (!hex) return null;
    let h = String(hex).replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hue = 0;
    let sat = 0;
    const light = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
            case g: hue = (b - r) / d + 2; break;
            default: hue = (r - g) / d + 4;
        }
        hue /= 6;
    }
    return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
};

export const SettingsProvider = ({ children }) => {
    const { user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [entitlements, setEntitlements] = useState(null);
    const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    const fetchSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/settings/`);
            setSettings(res.data);
        } catch (e) {
            console.error("Error fetching settings", e);
        }
    }, [BACKEND_URL]);

    const fetchEntitlements = useCallback(async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/saas/me`);
            setEntitlements(res.data);
        } catch (e) {
            // Fail-open on the client (backend still enforces); avoids hiding
            // modules due to a transient error or missing token on the login page.
            setEntitlements(null);
        } finally {
            setEntitlementsLoaded(true);
        }
    }, [BACKEND_URL]);

    // Only load tenant settings/entitlements when authenticated. Before login the
    // app must stay neutral (no tenant name/logo in the browser tab or favicon),
    // since /api/settings/ falls back to the first tenant when unauthenticated.
    useEffect(() => {
        if (user) {
            fetchSettings();
            fetchEntitlements();
        } else {
            setSettings(null);
            setEntitlements(null);
            setEntitlementsLoaded(true);
        }
    }, [user, fetchSettings, fetchEntitlements]);

    // hasModule: hide gated modules until entitlements are known (prevents a
    // flash of not-included modules right after login); fail-open on error.
    const hasModule = useCallback((moduleKey) => {
        if (!moduleKey) return true;
        if (!entitlementsLoaded) return false;
        if (!entitlements || !entitlements.features) return true; // fail-open
        return entitlements.features[moduleKey] !== false;
    }, [entitlements, entitlementsLoaded]);

    const updateSettings = async (data) => {
        try {
            const res = await axios.put(`${BACKEND_URL}/api/settings/`, data);
            setSettings(res.data);
            return res.data;
        } catch (e) {
            throw e;
        }
    };

    // --- Theming: apply the club's colors as CSS variables app-wide ---
    // Overrides shadcn --primary / --ring (so all primary buttons, focus rings,
    // active states pick up the club color) and exposes --club-primary/-secondary.
    useEffect(() => {
        const root = document.documentElement;
        const primary = settings?.primary_color || '#2563EB';
        const secondary = settings?.secondary_color || '#64748B';
        const primaryHsl = hexToHsl(primary);
        const secondaryHsl = hexToHsl(secondary);
        root.style.setProperty('--club-primary', primary);
        root.style.setProperty('--club-primary-foreground', '#ffffff');
        root.style.setProperty('--club-secondary', secondary);
        if (primaryHsl) {
            root.style.setProperty('--primary', primaryHsl);
            root.style.setProperty('--ring', primaryHsl);
        }
        if (secondaryHsl) {
            root.style.setProperty('--club-secondary-hsl', secondaryHsl);
        }
    }, [settings?.primary_color, settings?.secondary_color]);

    const getStatusLabel = (key) => {
        if (!settings || !settings.status_config) return key;
        const status = settings.status_config.find(s => s.key === key);
        return status ? status.label : key;
    };

    const getStatusColor = (key) => {
        if (!settings || !settings.status_config) return "bg-slate-100 text-slate-700";
        const status = settings.status_config.find(s => s.key === key);
        return status ? status.color : "bg-slate-100 text-slate-700";
    };

    const getResourceTypes = () => {
        return settings?.resource_types || ["Platz/Halle", "Raum", "Equipment"];
    };

    const getEventTypes = () => {
        return settings?.event_types || ["Training", "Spiel", "Fest", "Sonstiges"];
    };

    const getEventTypeColor = (type) => {
        return resolveEventColor(type, settings?.event_type_colors || {});
    };

    const getCustomRoles = () => {
        return settings?.custom_roles || ["admin", "leader", "member"];
    };

    const getDashboardTaskStatuses = () => {
        return settings?.dashboard_task_statuses || ["neu", "in_arbeit", "ueberfaellig"];
    };

    const normalizeRoles = (roleOrRoles) => {
        if (!roleOrRoles) return [];

        if (Array.isArray(roleOrRoles)) {
            return [...new Set(roleOrRoles.filter(Boolean))];
        }

        if (typeof roleOrRoles === 'object') {
            const merged = [
                ...(Array.isArray(roleOrRoles.roles) ? roleOrRoles.roles : []),
                ...(roleOrRoles.role ? [roleOrRoles.role] : [])
            ];
            return [...new Set(merged.filter(Boolean))];
        }

        return [roleOrRoles];
    };

    const normalizeRoleKey = (role) => String(role || '').trim().toLowerCase();

    const checkPermission = (roleOrRoles, permission) => {
        // Validate that the permission key exists in the registry
        if (process.env.NODE_ENV === 'development') {
            validatePermissionKey(permission);
        }

        const roles = normalizeRoles(roleOrRoles);

        // Die Rolle Admin hat IMMER auf alles Zugriff
        if (roles.includes('admin') || roles.includes('Admin') || roles.includes('ADMIN')) {
            return true;
        }

        if (!settings || !settings.role_permissions) return false;

        const normalizedRolePerms = {};
        Object.entries(settings.role_permissions).forEach(([roleName, perms]) => {
            const key = normalizeRoleKey(roleName);
            if (!key) return;
            if (!normalizedRolePerms[key]) {
                normalizedRolePerms[key] = new Set();
            }
            (perms || []).forEach((perm) => normalizedRolePerms[key].add(perm));
        });

        // Union permissions from ALL roles (additive - never blocking)
        const allPerms = new Set();
        for (const r of roles) {
            const perms = settings.role_permissions[r] || [];
            perms.forEach(p => allPerms.add(p));

            const normalizedPermSet = normalizedRolePerms[normalizeRoleKey(r)];
            if (normalizedPermSet) {
                normalizedPermSet.forEach(p => allPerms.add(p));
            }
        }
        return allPerms.has(permission);
    };

    return (
        <SettingsContext.Provider value={{ 
            settings, fetchSettings, updateSettings, 
            getStatusLabel, getStatusColor, getResourceTypes, getEventTypes, getEventTypeColor, 
            getCustomRoles, getDashboardTaskStatuses, checkPermission,
            entitlements, fetchEntitlements, hasModule, entitlementsLoaded
        }}>
            {children}
        </SettingsContext.Provider>
    );
};
